import { useEffect, useState } from 'react'
import { useSearchParams, Link, useNavigate } from 'react-router'
import { getBillingStatus, createCheckout, getPortalUrl, syncSubscription, updateSeats } from '@/lib/billing-api'
import { useAppLockStore } from '@/stores/app-lock.store'
import { useAuthStore } from '@/stores/auth.store'
import { appLockService } from '@/lib/app-lock'
import { authApi } from '@/lib/auth-api'
import { trackEvent, Events } from '@/lib/observability'
import { PinSetup } from '@/components/lock'
import { Capacitor } from '@capacitor/core'

interface BillingStatus {
  hasSubscription: boolean
  paidSeats: number
  allowed: number
  used: number
  remaining: number
  subscriptionId: string | null
}

export function ChildminderSettingsPage() {
  const navigate = useNavigate()
  const { accessToken, logout } = useAuthStore()
  const [searchParams, setSearchParams] = useSearchParams()
  const [billing, setBilling] = useState<BillingStatus | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [isUpgrading, setIsUpgrading] = useState(false)
  const [showSeatSelector, setShowSeatSelector] = useState(false)
  const [selectedSeats, setSelectedSeats] = useState(2)
  const [showAddMoreSeats, setShowAddMoreSeats] = useState(false)
  const [newSeatCount, setNewSeatCount] = useState(2)
  const [showChangePinSetup, setShowChangePinSetup] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [isDeleting, setIsDeleting] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  // Handle payment redirect
  useEffect(() => {
    const payment = searchParams.get('payment')
    if (payment === 'success') {
      setSuccessMessage('Payment successful! Your subscription has been updated.')
      setSearchParams({})
    } else if (payment === 'cancelled') {
      setError('Payment was cancelled.')
      setSearchParams({})
    }
  }, [searchParams, setSearchParams])

  // App Lock state
  const {
    biometricEnabled,
    biometricAvailable,
    biometryType,
    lockTimeout,
    isSetupComplete,
    setBiometricEnabled,
    setLockTimeout,
  } = useAppLockStore()

  const biometryName = appLockService.getBiometryTypeName(biometryType)
  const [biometricError, setBiometricError] = useState<string | null>(null)

  useEffect(() => {
    loadBillingStatus()
  }, [])

  const loadBillingStatus = async () => {
    try {
      setIsLoading(true)
      setError(null)
      // Auto-sync with Salable to ensure subscription is up to date
      await syncSubscription()
      const status = await getBillingStatus()
      setBilling(status)
      setSelectedSeats(Math.max(2, status.used + 1))
      setNewSeatCount(Math.max(2, status.paidSeats + 1))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load subscription status')
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpgrade = async () => {
    setIsUpgrading(true)
    setError(null)

    try {
      // Create new subscription via checkout
      trackEvent(Events.SUBSCRIPTION_UPGRADE_STARTED, { seats: selectedSeats })
      const { checkoutUrl } = await createCheckout(selectedSeats)
      window.location.href = checkoutUrl
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update subscription')
      setIsUpgrading(false)
    }
  }

  const handleManageSubscription = async () => {
    setIsUpgrading(true)
    setError(null)

    try {
      const { portalUrl } = await getPortalUrl()
      window.location.href = portalUrl
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open subscription portal')
      setIsUpgrading(false)
    }
  }

  const handleUpdateSeats = async () => {
    setIsUpgrading(true)
    setError(null)

    try {
      await updateSeats(newSeatCount)
      trackEvent(Events.SUBSCRIPTION_SEATS_CHANGED, { newSeatCount })
      setSuccessMessage('Subscription updated successfully!')
      setShowAddMoreSeats(false)
      await loadBillingStatus()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update subscription')
    } finally {
      setIsUpgrading(false)
    }
  }

  const handleToggleBiometric = async (enabled: boolean) => {
    setBiometricError(null)

    if (enabled) {
      const success = await appLockService.authenticateWithBiometric(
        `Verify ${biometryName} to enable it for app unlock`
      )

      if (success) {
        setBiometricEnabled(true)
      } else {
        setBiometricError(`${biometryName} verification failed. Please try again.`)
      }
    } else {
      setBiometricEnabled(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return
    if (!accessToken) return

    setIsDeleting(true)
    setError(null)

    try {
      await authApi.deleteAccount(accessToken)
      trackEvent(Events.ACCOUNT_DELETED)
      // Clear local state and redirect to login
      logout()
      navigate('/login', { replace: true })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete account')
      setIsDeleting(false)
    }
  }

  const handleExportData = async () => {
    if (!accessToken) return

    setIsExporting(true)
    setError(null)

    try {
      const blob = await authApi.exportData(accessToken)
      trackEvent(Events.DATA_EXPORTED)
      // Create download link
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `brightseed-data-export-${new Date().toISOString().split('T')[0]}.json`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      setSuccessMessage('Your data has been exported successfully')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export data')
    } finally {
      setIsExporting(false)
    }
  }

  const isNative = Capacitor.isNativePlatform()

  return (
    <div className="p-4">
      <h1 className="mb-4 text-2xl font-bold">Settings</h1>

      {successMessage && (
        <div className="mb-4 rounded bg-green-100 p-3 text-sm text-green-700">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="mb-4 rounded bg-red-100 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Profile Section */}
      <section className="mb-6 rounded-lg border border-gray-200 p-4">
        <h2 className="mb-3 text-lg font-medium">Your Profile</h2>
        <p className="mb-3 text-sm text-gray-500">
          Update your display name and change your password.
        </p>
        <Link
          to="/profile"
          className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          Edit Profile
        </Link>
      </section>

      {/* Manage Children Section */}
      <section className="mb-6 rounded-lg border border-gray-200 p-4">
        <h2 className="mb-3 text-lg font-medium">Children</h2>
        <p className="mb-3 text-sm text-gray-500">
          Add, edit, or remove child profiles and manage parent connections.
        </p>
        <Link
          to="/children"
          className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
          Manage Children
        </Link>
      </section>

      {/* Subscription Section */}
      <section className="mb-6 rounded-lg border border-gray-200 p-4">
        <h2 className="mb-3 text-lg font-medium">Subscription</h2>

        {isLoading ? (
          <div className="text-sm text-gray-500">Loading...</div>
        ) : billing ? (
          <div className="space-y-4">
            {/* Current Plan */}
            <div className="rounded-lg bg-gray-50 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">
                    {billing.hasSubscription
                      ? `${billing.paidSeats} child profile plan`
                      : 'Free Plan'}
                  </p>
                  <p className="text-sm text-gray-500">
                    {billing.hasSubscription
                      ? `£${billing.paidSeats * 2}/month`
                      : '1 child profile included'}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-gray-900">
                    {billing.used}/{billing.allowed}
                  </p>
                  <p className="text-sm text-gray-500">profiles used</p>
                </div>
              </div>

              {billing.remaining > 0 && (
                <p className="mt-3 text-sm text-green-600">
                  {billing.remaining} slot{billing.remaining !== 1 ? 's' : ''} remaining
                </p>
              )}

              {billing.remaining === 0 && (
                <p className="mt-3 text-sm text-amber-600">
                  No slots remaining - upgrade to add more
                </p>
              )}
            </div>

            {/* Actions */}
            {billing.hasSubscription ? (
              <div className="space-y-3">
                {/* Add More Seats Section */}
                {!showAddMoreSeats ? (
                  <button
                    onClick={() => setShowAddMoreSeats(true)}
                    disabled={isUpgrading}
                    className="w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                  >
                    Change Plan
                  </button>
                ) : (
                  <div className="rounded-lg border border-primary-200 bg-primary-50 p-4">
                    <label htmlFor="newSeats" className="mb-2 block text-sm font-medium text-gray-700">
                      How many child profiles do you need?
                    </label>
                    <select
                      id="newSeats"
                      value={newSeatCount}
                      onChange={(e) => setNewSeatCount(Number(e.target.value))}
                      className="mb-3 w-full rounded border border-gray-300 px-3 py-2"
                      disabled={isUpgrading}
                    >
                      {Array.from({ length: 99 }, (_, i) => i + 2).map((num) => {
                        const isCurrent = num === billing.paidSeats
                        return (
                          <option key={num} value={num} disabled={isCurrent}>
                            {num} child profiles - £{num * 2}/month
                            {isCurrent ? ' (current)' : ''}
                          </option>
                        )
                      })}
                    </select>
                    {newSeatCount < billing.paidSeats && newSeatCount >= billing.used && (
                      <p className="mb-3 text-sm text-amber-600">
                        Downgrading will reduce your monthly cost to £{newSeatCount * 2}/month
                      </p>
                    )}
                    {newSeatCount < billing.used && (
                      <div className="mb-3 rounded bg-amber-100 p-3 text-sm text-amber-800">
                        <strong>Warning:</strong> You have {billing.used} profiles but are selecting {newSeatCount} seats.
                        The {billing.used - newSeatCount} newest profile{billing.used - newSeatCount > 1 ? 's' : ''} will be locked (read-only).
                      </div>
                    )}
                    {newSeatCount > billing.paidSeats && (
                      <p className="mb-3 text-sm text-green-600">
                        Upgrading will increase your plan to £{newSeatCount * 2}/month (prorated)
                      </p>
                    )}
                    <div className="flex gap-2">
                      <button
                        onClick={handleUpdateSeats}
                        disabled={isUpgrading || newSeatCount === billing.paidSeats}
                        className="flex-1 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                      >
                        {isUpgrading ? 'Updating...' : newSeatCount < billing.paidSeats ? 'Downgrade' : 'Upgrade'}
                      </button>
                      <button
                        onClick={() => setShowAddMoreSeats(false)}
                        disabled={isUpgrading}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}

                {/* Manage Subscription button */}
                <button
                  onClick={handleManageSubscription}
                  disabled={isUpgrading}
                  className="w-full rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  {isUpgrading ? 'Loading...' : 'Manage Billing'}
                </button>
              </div>
            ) : (
              <>
                <button
                  onClick={() => setShowSeatSelector(true)}
                  disabled={isUpgrading}
                  className="w-full rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                >
                  Upgrade
                </button>

                {/* Seat Selector for new subscribers */}
                {showSeatSelector && (
                  <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50 p-4">
                    <label htmlFor="seats" className="mb-2 block text-sm font-medium text-gray-700">
                      How many child profiles do you need?
                    </label>
                    <select
                      id="seats"
                      value={selectedSeats}
                      onChange={(e) => setSelectedSeats(Number(e.target.value))}
                      className="mb-3 w-full rounded border border-gray-300 px-3 py-2"
                      disabled={isUpgrading}
                    >
                      {Array.from({ length: 99 }, (_, i) => i + 2).map((num) => (
                        <option key={num} value={num}>
                          {num} child profiles - £{num * 2}/month
                        </option>
                      ))}
                    </select>
                    <div className="flex gap-2">
                      <button
                        onClick={handleUpgrade}
                        disabled={isUpgrading}
                        className="flex-1 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                      >
                        {isUpgrading ? 'Loading...' : 'Continue to Payment'}
                      </button>
                      <button
                        onClick={() => setShowSeatSelector(false)}
                        disabled={isUpgrading}
                        className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}

            <p className="text-center text-xs text-gray-400">
              Secure payment via Stripe
            </p>
          </div>
        ) : null}
      </section>

      {/* Security Section */}
      {isNative && (
        <section className="rounded-lg border border-gray-200 p-4">
          <h2 className="mb-3 text-lg font-medium">Security</h2>
          <p className="mb-4 text-sm text-gray-500">
            Protect your account with a PIN or biometric authentication.
          </p>

          {/* App Lock Status */}
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="font-medium">App Lock</p>
              <p className="text-sm text-gray-500">
                PIN required to open the app
              </p>
            </div>
            <span className="rounded-full bg-green-100 px-3 py-1 text-sm font-medium text-green-700">
              Always On
            </span>
          </div>

          {/* Lock Timeout */}
          {isSetupComplete && (
            <>
              <div className="mb-4 flex items-center justify-between border-t border-gray-100 pt-4">
                <div>
                  <p className="font-medium">Lock after</p>
                  <p className="text-sm text-gray-500">
                    Time in background before locking
                  </p>
                </div>
                <select
                  value={lockTimeout}
                  onChange={(e) => setLockTimeout(Number(e.target.value))}
                  className="rounded border border-gray-300 px-3 py-1.5 text-sm"
                >
                  <option value={1}>1 minute</option>
                  <option value={2}>2 minutes</option>
                  <option value={5}>5 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                </select>
              </div>

              {/* Biometric Toggle */}
              {biometricAvailable && (
                <div className="mb-4 border-t border-gray-100 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Use {biometryName}</p>
                      <p className="text-sm text-gray-500">
                        Unlock faster with {biometryName}
                      </p>
                    </div>
                    <label className="relative inline-flex cursor-pointer items-center">
                      <input
                        type="checkbox"
                        checked={biometricEnabled}
                        onChange={(e) => handleToggleBiometric(e.target.checked)}
                        className="peer sr-only"
                      />
                      <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:ring-2 peer-focus:ring-primary-300"></div>
                    </label>
                  </div>
                  {biometricError && (
                    <p className="mt-2 text-sm text-red-600">{biometricError}</p>
                  )}
                </div>
              )}

              {/* Change PIN */}
              <div className="border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => setShowChangePinSetup(true)}
                  className="text-primary-600 font-medium text-sm"
                >
                  Change PIN
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {/* Change PIN Modal */}
      {showChangePinSetup && (
        <PinSetup
          onComplete={() => setShowChangePinSetup(false)}
        />
      )}

      {/* Legal & Data */}
      <section className="mb-6 rounded-lg border border-gray-200 p-4">
        <h2 className="mb-3 text-lg font-medium">Legal & Data</h2>
        <div className="space-y-3">
          <Link to="/privacy" className="block text-primary-600 hover:underline">
            Privacy Policy
          </Link>
          <Link to="/terms" className="block text-primary-600 hover:underline">
            Terms of Service
          </Link>
          <Link to="/cookies" className="block text-primary-600 hover:underline">
            Cookie Settings
          </Link>
          <div className="border-t border-gray-100 pt-3">
            <p className="mb-2 text-sm text-gray-600">
              Download a copy of all your data in JSON format.
            </p>
            <button
              onClick={handleExportData}
              disabled={isExporting}
              className="rounded border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {isExporting ? 'Exporting...' : 'Export My Data'}
            </button>
          </div>
        </div>
      </section>

      {/* Danger Zone - Account Deletion */}
      <section className="rounded-lg border border-red-200 bg-red-50 p-4">
        <h2 className="mb-3 text-lg font-medium text-red-700">Danger Zone</h2>

        {!showDeleteConfirm ? (
          <div>
            <p className="mb-3 text-sm text-gray-600">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="rounded-lg border border-red-300 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-100"
            >
              Delete Account
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded bg-red-100 p-3">
              <p className="font-medium text-red-800">Are you absolutely sure?</p>
              <p className="mt-1 text-sm text-red-700">
                This will permanently delete:
              </p>
              <ul className="mt-2 list-inside list-disc text-sm text-red-700">
                <li>Your account and profile</li>
                <li>All child profiles you've created</li>
                <li>All updates, notes, and photos</li>
                <li>All parent invitations</li>
                {billing?.hasSubscription && (
                  <li>Your subscription will be cancelled</li>
                )}
              </ul>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Type <span className="font-mono font-bold">DELETE</span> to confirm:
              </label>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full rounded border border-gray-300 px-3 py-2"
                placeholder="DELETE"
                disabled={isDeleting}
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleDeleteAccount}
                disabled={deleteConfirmText !== 'DELETE' || isDeleting}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {isDeleting ? 'Deleting...' : 'Permanently Delete Account'}
              </button>
              <button
                onClick={() => {
                  setShowDeleteConfirm(false)
                  setDeleteConfirmText('')
                }}
                disabled={isDeleting}
                className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  )
}
